/**
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Dashboard
 *| (C) Copyright IBM Corp. 2018, 2020
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */
define([
	'underscore',
	'dashboard-analytics/widgets/livewidget/nls/StringResources'
], function(_, StringResources) {
	/*
	* The purpose of this singleton is to compartmentalize each property into
	* its corresponding tab and section for the properties panel.
	*/
	const PropertiesSectionerV2 = (() => {

		/**
		 * Instance stores a reference to the Singleton
		 * @type { object }
		 */
		let instance = null;
		// The following is used to map sections to section position
		const sectionIdList = [
			'mapStyle',
			'regionLayer',
			'pointLayer',
			'latLongLayer',
			'colorPalette',
			'chart',
			'dataValueLabels',
			'itemAxis',
			'itemAxisTitle',
			'itemAxisLabel',
			'valueAxis',
			'valueAxisTitle',
			'valueAxisLabel',
			'columnValueAxis',
			'columnValueAxisTitle',
			'columnValueAxisLabel',
			'lineValueAxis',
			'lineValueAxisTitle',
			'lineValueAxisLabel',
			'legend',
			'title'
		];


		// The following maps a property to its section information.
		// Note, some props are commented out. They are unsupported at this time.
		const propertyIdToSectionMap = {
			// 'title' section
			'titleMode':{ sectionId:'title', sectionResource:'sectionName_title'  },

			// 'Data value labels' section (new section in Carbon design)
			'column.valueLabels.visible':{ sectionId:'dataValueLabels', sectionResource:'sectionName_dataValueLabels'  },
			'column.valueLabels.color':{ sectionId:'dataValueLabels', sectionResource:'sectionName_dataValueLabels'  },
			'valueLabels.format':{ sectionId:'dataValueLabels', sectionResource:'sectionName_dataValueLabels'  },
			'valueLabels.location':{ sectionId:'dataValueLabels', sectionResource:'sectionName_dataValueLabels'  },
			'valueLabels.color':{ sectionId:'dataValueLabels', sectionResource:'sectionName_dataValueLabels'  },
			'valueLabels.size':{ sectionId:'dataValueLabels', sectionResource:'sectionName_dataValueLabels'  },
			'contrast.label.color':{ sectionId:'dataValueLabels', sectionResource:'sectionName_dataValueLabels'  },
			'valueLabels.orientation':{ sectionId:'dataValueLabels', sectionResource:'sectionName_dataValueLabels'  },
			'valueLabels.visible':{ sectionId:'dataValueLabels', sectionResource:'sectionName_dataValueLabels'  },
			'label.shadow':{ sectionId:'dataValueLabels', sectionResource:'sectionName_dataValueLabels'  },
			'contrastText':{ sectionId:'dataValueLabels', sectionResource:'sectionName_dataValueLabels'  },
			'lineWithPoints.valueLabels.visible':{ sectionId:'dataValueLabels', sectionResource:'sectionName_dataValueLabels'  },
			'lineWithPoints.valueLabels.color':{ sectionId:'dataValueLabels', sectionResource:'sectionName_dataValueLabels'  },
			'labels.format':{ sectionId:'dataValueLabels', sectionResource:'sectionName_dataValueLabels'  },
			'label.percentage':{ sectionId:'dataValueLabels', sectionResource:'sectionName_dataValueLabels'  },
			'labels.visible':{ sectionId:'dataValueLabels', sectionResource:'sectionName_dataValueLabels'  },
			'labelLocation':{ sectionId:'dataValueLabels', sectionResource:'sectionName_dataValueLabels'  },
			'contrast.axes.color':{ sectionId:'dataValueLabels', sectionResource:'sectionName_dataValueLabels'  },
			'contrast.text.color':{ sectionId:'dataValueLabels', sectionResource:'sectionName_dataValueLabels'  },

			// 'X axis' section
			'itemAxis.minValue': {  sectionId: 'itemAxis', sectionResource:'sectionName_xAxis'  },
			'itemAxis.maxValue': {  sectionId: 'itemAxis', sectionResource:'sectionName_xAxis'  },
			'itemAxis.alignment':{  sectionId: 'itemAxis', sectionResource:'sectionName_xAxis'  },
			'gridLines.visible':{ sectionId:'itemAxis', sectionResource:'sectionName_xAxis'  },
			'itemAxis.labels.vertical':{ sectionId:'itemAxis', sectionResource:'sectionName_xAxis'  },
			'itemAxis.gridLines.visible':{ sectionId:'itemAxis', sectionResource:'sectionName_xAxis'  },

			// Carbon design doesn't have section "axis", may be no longer needed when FF 'PropertiesV2' is enabled.
			// TODO: to be deleted when the property test of all chart types are done and none of these properties are used in new design.
			// 'axis.label.color':{  sectionId: 'itemAxis', sectionResource:'sectionName_xAxis'  },
			// 'axis.title.color':{  sectionId: 'itemAxis', sectionResource:'sectionName_xAxis'  },
			// 'axis.title.visible':{  sectionId: 'itemAxis', sectionResource:'sectionName_xAxis'  },
			// 'axis.indicators.visible':{  sectionId: 'itemAxis', sectionResource:'sectionName_xAxis'  },
			// 'axis.labels.visible':{  sectionId: 'itemAxis', sectionResource:'sectionName_xAxis'  },
			// 'axis.x.alignment':{  sectionId: 'itemAxis', sectionResource:'sectionName_xAxis'  },

			// 'X axis title' section
			'itemAxis.title.visible': {  sectionId: 'itemAxisTitle', sectionResource:'sectionName_xAxis_title' },
			'itemAxis.title':{  sectionId: 'itemAxisTitle', sectionResource:'sectionName_xAxis_title'  },
			'itemAxis.title.font':{  sectionId: 'itemAxisTitle', sectionResource:'sectionName_xAxis_title'  },
			'itemAxis.title.color':{  sectionId: 'itemAxisTitle', sectionResource:'sectionName_xAxis_title'  },

			// 'X axis labels' section
			'itemAxis.labels.visible':{  sectionId: 'itemAxisLabel', sectionResource:'sectionName_xAxis_label'  },
			'itemAxis.labels.layoutMode':{  sectionId: 'itemAxisLabel', sectionResource:'sectionName_xAxis_label'  },
			'itemAxis.ticks.labels.visible':{  sectionId: 'itemAxisLabel', sectionResource:'sectionName_xAxis_label'  },
			'itemAxis.ticks.visible':{  sectionId: 'itemAxisLabel', sectionResource:'sectionName_xAxis_label'  },
			'itemAxis.tickInterval':{  sectionId: 'itemAxisLabel', sectionResource:'sectionName_xAxis_label'  },
			'itemAxis.ticks.labels.font':{  sectionId: 'itemAxisLabel', sectionResource:'sectionName_xAxis_label'  },
			'itemAxis.ticks.labels.layoutMode': {  sectionId: 'itemAxisLabel', sectionResource:'sectionName_xAxis_label'  },
			'itemAxis.ticks.labels.color':{  sectionId: 'itemAxisLabel', sectionResource:'sectionName_xAxis_label'  },

			// 'Y axis' section
			'maintainAxisScales': {  sectionId: 'valueAxis', sectionResource:'sectionName_yAxis'  },
			'valueAxis.logarithmic':{ sectionId:'valueAxis', sectionResource:'sectionName_yAxis'  },
			'valueAxis.minValue': {  sectionId: 'valueAxis', sectionResource:'sectionName_yAxis'  },
			'valueAxis.maxValue': {  sectionId: 'valueAxis', sectionResource:'sectionName_yAxis'  },
			'valueAxis.zeroOrigin':{ sectionId:'valueAxis', sectionResource:'sectionName_yAxis'  },
			'valueAxis.ticks.visible':{  sectionId: 'valueAxis', sectionResource:'sectionName_yAxis'  },
			'valueAxis.tickInterval':{  sectionId: 'valueAxis', sectionResource:'sectionName_yAxis'  },
			'valueAxis.gridLines.visible':{ sectionId:'valueAxis', sectionResource:'sectionName_yAxis'  },

			// 'Y axis title' section
			'valueAxis.title.visible': {  sectionId: 'valueAxisTitle', sectionResource:'sectionName_yAxis_title'  },
			'valueAxis.title':{  sectionId: 'valueAxisTitle', sectionResource:'sectionName_yAxis_title'  },
			'valueAxis.title.font':{  sectionId: 'valueAxisTitle', sectionResource:'sectionName_yAxis_title'  },
			'valueAxis.title.color':{  sectionId: 'valueAxisTitle', sectionResource:'sectionName_yAxis_title'  },

			// 'Y axis label' section
			'valueAxis.labels.visible':{  sectionId: 'valueAxisLabel', sectionResource:'sectionName_yAxis_label'  },
			'valueAxis.ticks.labels.visible':{  sectionId: 'valueAxisLabel', sectionResource:'sectionName_yAxis_label'  },
			'valueAxis.ticks.labels.font':{  sectionId: 'valueAxisLabel', sectionResource:'sectionName_yAxis_label'  },
			'valueAxis.ticks.labels.color':{  sectionId: 'valueAxisLabel', sectionResource:'sectionName_yAxis_label'  },

			// Line and column properties
			'valueAxis.column.title.visible': {  sectionId: 'columnValueAxisTitle', sectionResource:'sectionName_column_yAxis_title'  },
			'valueAxis.column.title':{  sectionId: 'columnValueAxisTitle', sectionResource:'sectionName_column_yAxis_title'  },
			'valueAxis.column.title.color':{  sectionId: 'columnValueAxisTitle', sectionResource:'sectionName_column_yAxis_title'  },
			'valueAxis.column.title.font':{  sectionId: 'columnValueAxisTitle', sectionResource:'sectionName_column_yAxis_title'  },
			'valueAxis.column.ticks.labels.visible':{  sectionId: 'columnValueAxisLabel', sectionResource:'sectionName_column_yAxis_label'  },
			'valueAxis.column.ticks.visible':{  sectionId: 'columnValueAxisLabel', sectionResource:'sectionName_column_yAxis_label'  },
			'valueAxis.column.ticks.labels.font':{  sectionId: 'columnValueAxisLabel', sectionResource:'sectionName_column_yAxis_label'  },
			'valueAxis.column.ticks.labels.color':{  sectionId: 'columnValueAxisLabel', sectionResource:'sectionName_column_yAxis_label'  },
			'valueAxis.column.minValue': {  sectionId: 'columnValueAxis', sectionResource:'sectionName_column_yAxis'  },
			'valueAxis.column.maxValue': {  sectionId: 'columnValueAxis', sectionResource:'sectionName_column_yAxis'  },
			'valueAxis.column.logarithmic':{ sectionId: 'columnValueAxis', sectionResource:'sectionName_column_yAxis'  },

			'valueAxis.line.title.visible': {  sectionId: 'lineValueAxisTitle', sectionResource:'sectionName_line_yAxis_title'  },
			'valueAxis.line.title':{  sectionId: 'lineValueAxisTitle', sectionResource:'sectionName_line_yAxis_title'  },
			'valueAxis.line.title.color':{  sectionId: 'lineValueAxisTitle', sectionResource:'sectionName_line_yAxis_title'  },
			'valueAxis.line.title.font':{  sectionId: 'lineValueAxisTitle', sectionResource:'sectionName_line_yAxis_title'  },
			'valueAxis.line.ticks.labels.visible':{  sectionId: 'lineValueAxisLabel', sectionResource:'sectionName_line_yAxis_label'  },
			'valueAxis.line.ticks.visible':{  sectionId: 'lineValueAxisLabel', sectionResource:'sectionName_line_yAxis_label'  },
			'valueAxis.line.ticks.labels.font':{  sectionId: 'lineValueAxisLabel', sectionResource:'sectionName_line_yAxis_label'  },
			'valueAxis.line.ticks.labels.color':{  sectionId: 'lineValueAxisLabel', sectionResource:'sectionName_line_yAxis_label'  },
			'valueAxis.line.minValue': {  sectionId: 'lineValueAxis', sectionResource:'sectionName_line_yAxis'  },
			'valueAxis.line.maxValue': {  sectionId: 'lineValueAxis', sectionResource:'sectionName_line_yAxis'  },
			'valueAxis.line.logarithmic':{ sectionId:'lineValueAxis', sectionResource:'sectionName_line_yAxis'  },

			// colorPalette section
			'heatScalePalette': { sectionId:'colorPalette', sectionResource:'sectionName_colorPalette'  },
			'colorPalette': { sectionId:'colorPalette', sectionResource:'sectionName_colorPalette'  },
			'contColorPalette': { sectionId:'colorPalette', sectionResource:'sectionName_colorPalette'  },
			'defaultPaletteIndex': { sectionId:'colorPalette', sectionResource:'sectionName_colorPalette'  },
			'lineColor': { sectionId:'colorPalette', sectionResource:'sectionName_colorPalette'  },
			'fromColor': { sectionId:'colorPalette', sectionResource:'sectionName_colorPalette'  },
			'goal_color': { sectionId:'colorPalette', sectionResource:'sectionName_colorPalette'  },
			'compositeLineColor': { sectionId:'colorPalette', sectionResource:'sectionName_colorPalette'  },
			'backgroundColor':{ sectionId:'colorPalette', sectionResource:'sectionName_colorPalette'  },
			'colorPalette_column':{ sectionId:'colorPalette', sectionResource:'sectionName_colorPalette'  },
			'colorPalette_column_defaultIndex':{ sectionId:'colorPalette', sectionResource:'sectionName_colorPalette'  },
			'contColorPalette_column':{ sectionId:'colorPalette', sectionResource:'sectionName_colorPalette'  },
			'colorPalette_line':{ sectionId:'colorPalette', sectionResource:'sectionName_colorPalette'  },
			'colorPalette_line_defaultIndex':{ sectionId:'colorPalette', sectionResource:'sectionName_colorPalette'  },
			'tableAreaBackground':{ sectionId:'colorPalette', sectionResource:'sectionName_colorPalette'  },
			'tableContentBackground':{ sectionId:'colorPalette', sectionResource:'sectionName_colorPalette'  },
			'color.min':{ sectionId:'colorPalette', sectionResource:'sectionName_colorPalette'  },
			'color.mid':{ sectionId:'colorPalette', sectionResource:'sectionName_colorPalette'  },
			'color.max':{ sectionId:'colorPalette', sectionResource:'sectionName_colorPalette'  },
			'network.links.color':{ sectionId:'colorPalette', sectionResource:'sectionName_colorPalette'  },
			'totalColumn.positive.color':{ sectionId:'colorPalette', sectionResource:'sectionName_colorPalette'  },
			'totalColumn.negative.color':{ sectionId:'colorPalette', sectionResource:'sectionName_colorPalette'  },
			'firstColumn.positive.color':{ sectionId:'colorPalette', sectionResource:'sectionName_colorPalette'  },
			'firstColumn.negative.color':{ sectionId:'colorPalette', sectionResource:'sectionName_colorPalette'  },
			'column.positive.color':{ sectionId:'colorPalette', sectionResource:'sectionName_colorPalette'  },
			'column.negative.color':{ sectionId:'colorPalette', sectionResource:'sectionName_colorPalette'  },

			// legend section
			'widget.legend.position':{ sectionId:'legend', sectionResource:'sectionName_legend'  },
			'widget.legend.titleVisible':{ sectionId:'legend', sectionResource:'sectionName_legend'  },
			'widget.legend.display':{ sectionId:'legend', sectionResource:'sectionName_legend'  },
			'widget.legend.splitter':{ sectionId:'legend', sectionResource:'sectionName_legend' },
			'widget.legend.aggregateTypeVisible':{ sectionId:'legend', sectionResource:'sectionName_legend'  },
			'color.title':{ sectionId:'legend', sectionResource:'sectionName_legend'  },
			'pointcolor.title':{ sectionId:'legend', sectionResource:'sectionName_legend'  },
			'series.title':{ sectionId:'legend', sectionResource:'sectionName_legend'  },
			'values.title':{ sectionId:'legend', sectionResource:'sectionName_legend'  },
			'categories.title':{ sectionId:'legend', sectionResource:'sectionName_legend'  },
			'size.title':{ sectionId:'legend', sectionResource:'sectionName_legend'  },
			'columnColor.title':{ sectionId:'legend', sectionResource:'sectionName_legend'  },
			'lineColor.title':{ sectionId:'legend', sectionResource:'sectionName_legend'  },
			'widget.legend.color':{ sectionId:'legend', sectionResource:'sectionName_legend'  },
			'widget.legend.font':{ sectionId:'legend', sectionResource:'sectionName_legend'  },
			// 'widget.legend.titleColor':{ sectionId:'legend', sectionResource:'sectionName_legend'  },
			'totalColumn.legend.label.positive':{ sectionId:'legend', sectionResource:'sectionName_legend'  },
			'totalColumn.legend.label.negative':{ sectionId:'legend', sectionResource:'sectionName_legend'  },
			'firstColumn.positive.label':{ sectionId:'legend', sectionResource:'sectionName_legend'  },
			'firstColumn.negative.label':{ sectionId:'legend', sectionResource:'sectionName_legend'  },
			'value.positive.label':{ sectionId:'legend', sectionResource:'sectionName_legend'  },
			'value.negative.label':{ sectionId:'legend', sectionResource:'sectionName_legend'  },

			// chart section
			'gridlineColor':{ sectionId:'chart', sectionResource:'sectionName_chart'  },
			'labelColor':{ sectionId:'chart', sectionResource:'sectionName_chart'  },
			'optimizeSize':{ sectionId:'chart', sectionResource:'sectionName_chart'  },
			'mapLabels.localLanguage':{ sectionId:'chart', sectionResource:'sectionName_chart'  },
			'border.width':{ sectionId:'chart', sectionResource:'sectionName_chart'  },
			'border.color':{ sectionId:'chart', sectionResource:'sectionName_chart'  },
			'transpose':{ sectionId:'chart', sectionResource:'sectionName_chart'  },
			'area.interpolate':{ sectionId:'chart', sectionResource:'sectionName_chart'  },
			'data.handling':{ sectionId:'chart', sectionResource:'sectionName_chart'  },
			'targetVisibility':{ sectionId:'chart', sectionResource:'sectionName_chart'  },
			'target.title.visible':{ sectionId:'chart', sectionResource:'sectionName_chart'  },
			'target.title.color':{ sectionId:'chart', sectionResource:'sectionName_chart'  },
			'target.marker.color': { sectionId:'chart', sectionResource:'sectionName_chart'  },
			'target.marker.thickness': { sectionId:'chart', sectionResource:'sectionName_chart'  },
			'effect.duration':{ sectionId:'chart', sectionResource:'sectionName_chart'  },
			'showMinimap':{ sectionId:'chart', sectionResource:'sectionName_chart'  },
			'actions':{ sectionId:'chart', sectionResource:'sectionName_chart'  },
			'showInsights':{ sectionId:'chart', sectionResource:'sectionName_chart'  },
			'treeMode':{ sectionId:'chart', sectionResource:'sectionName_chart'  },
			'topBottomN':{ sectionId:'chart', sectionResource:'sectionName_chart'  },
			'topBottomTargetValue':{ sectionId:'chart', sectionResource:'sectionName_chart'  },
			'leaf.labels.visible':{ sectionId:'chart', sectionResource:'sectionName_chart'  },
			'donutRadius':{ sectionId:'chart', sectionResource:'sectionName_chart'  },
			'sort':{ sectionId:'chart', sectionResource:'sectionName_chart'  },
			'dataHandling':{ sectionId:'chart', sectionResource:'sectionName_chart'  },
			'symbolShape':{ sectionId:'chart', sectionResource:'sectionName_chart'  },
			'stacked.percent':{ sectionId:'chart', sectionResource:'sectionName_chart'  },
			'gridlines.visible':{ sectionId:'chart', sectionResource:'sectionName_chart'  },
			'column.border.width':{ sectionId:'chart', sectionResource:'sectionName_chart'  },
			'column.border.color':{ sectionId:'chart', sectionResource:'sectionName_chart'  },
			'lines.visible':{ sectionId:'chart', sectionResource:'sectionName_chart'  },
			'markers.visible':{ sectionId:'chart', sectionResource:'sectionName_chart'  },
			'lineWithPoints.interpolate':{ sectionId:'chart', sectionResource:'sectionName_chart'  },
			'lineWithPoints.symbol':{ sectionId:'chart', sectionResource:'sectionName_chart'  },
			'line.interpolate':{ sectionId:'chart', sectionResource:'sectionName_chart'  },
			'effect.entry.line.type':{ sectionId:'chart', sectionResource:'sectionName_chart'  },
			'effect.entry.line.delay':{ sectionId:'chart', sectionResource:'sectionName_chart'  },
			'effect.entry.line.stagger':{ sectionId:'chart', sectionResource:'sectionName_chart'  },
			'effect.entry.line.duration':{ sectionId:'chart', sectionResource:'sectionName_chart'  },
			'word.orientation':{ sectionId:'chart', sectionResource:'sectionName_chart'  },
			'dial.roundedEnds':{ sectionId:'chart', sectionResource:'sectionName_chart'  },
			'dial.maxValue':{ sectionId:'chart', sectionResource:'sectionName_chart'  },
			'dial.propotionalwidths':{ sectionId:'chart', sectionResource:'sectionName_chart'  },
			'dial.showAsPercentage':{ sectionId:'chart', sectionResource:'sectionName_chart'  },
			'bar.padding':{ sectionId:'chart', sectionResource:'sectionName_chart'  },
			'bar.relativePadding':{ sectionId:'chart', sectionResource:'sectionName_chart'  },
			'bar.direction.vertical':{ sectionId:'chart', sectionResource:'sectionName_chart'  },
			'stacked':{ sectionId:'chart', sectionResource:'sectionName_chart'  },
			'effect.entry.bar.type':{ sectionId:'chart', sectionResource:'sectionName_chart'  },
			'effect.entry.bar.delay':{ sectionId:'chart', sectionResource:'sectionName_chart'  },
			'effect.entry.bar.stagger':{ sectionId:'chart', sectionResource:'sectionName_chart'  },
			'effect.entry.bar.duration':{ sectionId:'chart', sectionResource:'sectionName_chart'  },
			'lines.smooth':{ sectionId:'chart', sectionResource:'sectionName_chart'  },
			'sortX':{ sectionId:'chart', sectionResource:'sectionName_chart'  },
			'useSingleAxis':{ sectionId:'chart', sectionResource:'sectionName_chart'  },
			'syncAxes':{ sectionId:'chart', sectionResource:'sectionName_chart'  },
			'lineWithPoints.display':{ sectionId:'chart', sectionResource:'sectionName_chart'  },
			'swapLineColumn':{ sectionId:'chart', sectionResource:'sectionName_chart'  },
			'autoZoom':{ sectionId:'chart', sectionResource:'sectionName_chart'  },
			'maxZoom':{ sectionId:'chart', sectionResource:'sectionName_chart'  },
			'views.hidden':{ sectionId:'chart', sectionResource:'sectionName_chart'  },
			'enableLayerControl':{ sectionId:'chart', sectionResource:'sectionName_chart'  },
			'enableFeatureSearchControl':{ sectionId:'chart', sectionResource:'sectionName_chart'  },
			'totalColumn.visible':{ sectionId:'chart', sectionResource:'sectionName_chart' },
			'totalColumn.label':{ sectionId:'chart', sectionResource:'sectionName_chart' },

			// Map-related sections
			'map.style.2':{ sectionId:'mapStyle', sectionResource:'sectionName_style'  },
			'map.style.url':{ sectionId:'mapStyle', sectionResource:'sectionName_style'  },
			'region.visible':{ sectionId:'regionLayer', sectionResource:'sectionName_regionLayer'  },
			'region.transparency':{ sectionId:'regionLayer', sectionResource:'sectionName_regionLayer'  },
			'region.custom.mapId':{ sectionId:'regionLayer', sectionResource:'sectionName_regionLayer'  },
			'region.custom.layerName':{ sectionId:'regionLayer', sectionResource:'sectionName_regionLayer'  },
			'region.custom.propertyName':{ sectionId:'regionLayer', sectionResource:'sectionName_regionLayer'  },
			'locationColor.title':{ sectionId:'regionLayer', sectionResource:'sectionName_regionLayer'  },
			'colorPalette_data.region': { sectionId:'regionLayer', sectionResource:'sectionName_regionLayer'  },
			'contColorPalette_data.region': { sectionId:'regionLayer', sectionResource:'sectionName_regionLayer'  },
			'Mapbox.secretToken':{ sectionId:'regionLayer', sectionResource:'sectionName_regionLayer'  },
			'Mapbox.accountName':{ sectionId:'regionLayer', sectionResource:'sectionName_regionLayer'  },
			'Mapbox.token':{ sectionId:'regionLayer', sectionResource:'sectionName_regionLayer'  },

			'point.visible':{ sectionId:'pointLayer', sectionResource:'sectionName_pointLayer'  },
			'point.defaultSize':{ sectionId:'pointLayer', sectionResource:'sectionName_pointLayer'  },
			'point.minSize':{ sectionId:'pointLayer', sectionResource:'sectionName_pointLayer'  },
			'point.maxSize':{ sectionId:'pointLayer', sectionResource:'sectionName_pointLayer'  },
			'point.transparency':{ sectionId:'pointLayer', sectionResource:'sectionName_pointLayer'  },
			'point.staticZeroValueSize':{ sectionId:'pointLayer', sectionResource:'sectionName_pointLayer'  },
			'point.custom.mapId':{ sectionId:'pointLayer', sectionResource:'sectionName_pointLayer'  },
			'point.custom.layerName':{ sectionId:'pointLayer', sectionResource:'sectionName_pointLayer'  },
			'point.custom.propertyName':{ sectionId:'pointLayer', sectionResource:'sectionName_pointLayer'  },
			'point.grouping.mode':{ sectionId:'pointLayer', sectionResource:'sectionName_pointLayer'  },
			'point.grouping.transition':{ sectionId:'pointLayer', sectionResource:'sectionName_pointLayer'  },
			'point.grouping.heat.weight':{ sectionId:'pointLayer', sectionResource:'sectionName_pointLayer'  },
			'point.grouping.cluster.radius':{ sectionId:'pointLayer', sectionResource:'sectionName_pointLayer'  },
			'point.grouping.cluster.groups':{ sectionId:'pointLayer', sectionResource:'sectionName_pointLayer'  },
			'pointSize.title':{ sectionId:'pointLayer', sectionResource:'sectionName_pointLayer'  },
			'pointColor.title':{ sectionId:'pointLayer', sectionResource:'sectionName_pointLayer'  },
			'point.sizeScaling':{ sectionId:'pointLayer', sectionResource:'sectionName_pointLayer'  },
			'colorPalette_data.point': { sectionId:'pointLayer', sectionResource:'sectionName_pointLayer'  },
			'contColorPalette_data.point': { sectionId:'pointLayer', sectionResource:'sectionName_pointLayer'  },

			'latlongSize.title':{ sectionId:'latLongLayer', sectionResource:'sectionName_latLongLayer'  },
			'latlongColor.title':{ sectionId:'latLongLayer', sectionResource:'sectionName_latLongLayer'  },
			'latLong.visible':{ sectionId:'latLongLayer', sectionResource:'sectionName_latLongLayer'  },
			'latLong.defaultSize':{ sectionId:'latLongLayer', sectionResource:'sectionName_latLongLayer'  },
			'latLong.minSize':{ sectionId:'latLongLayer', sectionResource:'sectionName_latLongLayer'  },
			'latLong.maxSize':{ sectionId:'latLongLayer', sectionResource:'sectionName_latLongLayer'  },
			'latLong.transparency':{ sectionId:'latLongLayer', sectionResource:'sectionName_latLongLayer'  },
			'latLong.staticZeroValueSize':{ sectionId:'latLongLayer', sectionResource:'sectionName_latLongLayer'  },
			'latLong.grouping.mode':{ sectionId:'latLongLayer', sectionResource:'sectionName_latLongLayer'  },
			'latLong.grouping.transition':{ sectionId:'latLongLayer', sectionResource:'sectionName_latLongLayer'  },
			'latLong.grouping.heat.weight':{ sectionId:'latLongLayer', sectionResource:'sectionName_latLongLayer'  },
			'latLong.grouping.cluster.radius':{ sectionId:'latLongLayer', sectionResource:'sectionName_latLongLayer'  },
			'latLong.grouping.cluster.groups':{ sectionId:'latLongLayer', sectionResource:'sectionName_latLongLayer'  },
			'colorPalette_data.latlong': { sectionId:'latLongLayer', sectionResource:'sectionName_latLongLayer'  },
			'contColorPalette_data.latlong': { sectionId:'latLongLayer', sectionResource:'sectionName_latLongLayer'  }
		};

		let sectionPosition = 20;
		const sections = {
			'dataValueLabels': { active: true, toggable: true, id:  'dataValueLabels', label: StringResources.get('sectionName_dataValueLabels'),  type: 'Section', position: sectionPosition++ },
			'itemAxis':  { active: true, toggable: false, id:  'itemAxis', label: StringResources.get('sectionName_xAxis'),  type: 'Section', position: sectionPosition++ },
			'itemAxisTitle':  { active: true, toggable: true, id:  'itemAxisTitle', label: StringResources.get('sectionName_xAxis_title'),  type: 'Section', position: sectionPosition++ },
			'itemAxisLabel':  { active: true, toggable: true, id:  'itemAxisLabel', label: StringResources.get('sectionName_xAxis_label'),  type: 'Section',  position: sectionPosition++ },
			'valueAxis':  { active: true, toggable: false, id:  'valueAxis', label: StringResources.get('sectionName_yAxis'),  type: 'Section', position: sectionPosition++ },
			'valueAxisTitle': { active: true, toggable: true, id:  'valueAxisTitle', label: StringResources.get('sectionName_yAxis_title'),  type: 'Section',  position: sectionPosition++ },
			'valueAxisLabel': { active: true, toggable: true, id:  'valueAxisLabel', label: StringResources.get('sectionName_yAxis_label'),  type: 'Section',  position: sectionPosition++ },
			'columnValueAxis':  { active: true, toggable: false, id:  'columnValueAxis', label: StringResources.get('sectionName_column_yAxis'),  type: 'Section', position: sectionPosition++ },
			'columnValueAxisTitle': { active: true, toggable: true, id:  'columnValueAxisTitle', label: StringResources.get('sectionName_column_yAxis_title'),  type: 'Section',  position: sectionPosition++ },
			'columnValueAxisLabel': { active: true, toggable: true, id:  'columnValueAxisLabel', label: StringResources.get('sectionName_column_yAxis_label'),  type: 'Section',  position: sectionPosition++ },
			'lineValueAxis':  { active: true, toggable: false, id:  'lineValueAxis', label: StringResources.get('sectionName_line_yAxis'),  type: 'Section', position: sectionPosition++ },
			'lineValueAxisTitle': { active: true, toggable: true, id:  'lineValueAxisTitle', label: StringResources.get('sectionName_line_yAxis_title'),  type: 'Section',  position: sectionPosition++ },
			'lineValueAxisLabel': { active: true, toggable: true, id:  'lineValueAxisLabel', label: StringResources.get('sectionName_line_yAxis_label'),  type: 'Section',  position: sectionPosition++ },
			'legend': { active: true, toggable: true, id:  'legend', label: StringResources.get('sectionName_legend'),  type: 'Section',  position: sectionPosition++ },
			'positionAndStyle': { active: true, toggable: true, id:  'positionAndStyle', label: StringResources.get('sectionName_positionAndStyle'),  type: 'Section',  position: sectionPosition++ },
			'title': { active: true, toggable: true, id:  'title', label: StringResources.get('sectionName_title'),  type: 'Section',  position: sectionPosition++ }
		};

		const itemValueAxisCaptionsMap = {
			itemAxis: 'sectionName_yAxis',
			itemAxisTitle: 'sectionName_yAxis_title',
			itemAxisLabel: 'sectionName_yAxis_label',
			valueAxis: 'sectionName_xAxis',
			valueAxisTitle: 'sectionName_xAxis_title',
			valueAxisLabel: 'sectionName_xAxis_label'
		};
		/**
		 * For bar and stacked bar charts, we reserve the x/y section name.
		 * i.e. length slot's properties will show under "x-axis" for bar/stacked bar charts; show under "y-axis" for column/stacked column
		 */
		const getSectionName = (id, key, isItemAxisYAxis) => {
			let resourceKey = key;
			if(isItemAxisYAxis) {
				resourceKey = itemValueAxisCaptionsMap[id] || resourceKey;
			}
			return StringResources.get(resourceKey);
		};

		function init() {
			return {

				/**
				* @param {String} id of the property of interest
				* @param {String} [visType] of the content
				*/
				getSectionInformationForPropertyId: function(id, isItemAxisYAxis) {
					let sectionInfo;
					// If its in the map return its content.
					if (propertyIdToSectionMap && propertyIdToSectionMap[id]) {
						sectionInfo = propertyIdToSectionMap[id];
						sectionInfo['sectionId'] = sectionInfo.sectionId;
						sectionInfo['sectionName'] = getSectionName(sectionInfo.sectionId, sectionInfo.sectionResource, isItemAxisYAxis);
						sectionInfo['sectionPosition'] = sectionIdList.indexOf(sectionInfo.sectionId.split('.')[0]) + 1;//1-based as 0 is sorted last and not first
						return sectionInfo;
					} else {
						sectionInfo = {};
						sectionInfo['sectionId'] = 'chart';
						sectionInfo['sectionName'] = StringResources.get('sectionName_chart');
						sectionInfo['sectionPosition'] = sectionIdList.indexOf(sectionInfo.sectionId.split('.')[0]) + 1;
						return sectionInfo;
					}
				},

				/**
				* @param {String} id of the property of interest
				*/
				getSection: function(id){
					return sections[id];
				}
			};
		}

		return {

			// Get the Singleton instance if one exists
			// or create one if it doesn't
			getInstance: function () {

				if (!instance) {
					instance = init();
				}

				return instance;
			}
		};

	})();
	return PropertiesSectionerV2;
});
